import { useState } from 'react';

import { ChevronLeft, ChevronRight, FirstPage, Refresh } from '@mui/icons-material';
import {
    Autocomplete,
    AutocompleteChangeReason,
    AutocompleteProps,
    Box,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    SxProps,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';

import { CissTextField } from 'components/fields/';
import { RequestOptionsType, useRequestListagem } from 'hooks/useRequestListagem';

const REGEX_REMOVE_BRACES = /[{}]+/g;
const REGEX_GET_KEY_COLUMN = /({[\w.]{1,}})/g;

/**
 * Recebe um template de string e substitui as chaves pelo valor correspondente do objeto option.
 */
const getValueFromTemplate = (template: string, option: any): string => {
    if (!template) return option;

    return template.replace(REGEX_GET_KEY_COLUMN, (match): string => {
        const keyColumn = match.replace(REGEX_REMOVE_BRACES, '');

        let value = option;

        keyColumn.split('.').forEach((key) => (value = value[key]));

        return value ?? match;
    });
};

export interface CissAutoCompleteProps {
    // identificação
    name?: string;
    label?: string;
    queryKey?: string;
    objectId?: string;
    // valores e opções
    value: unknown;
    options?: Array<string | number | object | boolean>;
    requestOptions?: RequestOptionsType | null;
    // estados
    disabled?: boolean;
    readOnly?: boolean;
    multiple?: boolean;
    disableClearable?: boolean;
    disableCloseOnSelect?: boolean;
    // estilo e layout
    sx?: SxProps;
    size?: 'small' | 'medium' | undefined;
    error?: string;
    // formatação de strings
    selectedStringTemplate?: string;
    optionStringTemplate?: string;
    getOptionString?: (option: any) => string;
    getSelectedString?: (option: any) => string;
    // callbacks e funções
    onChange: (e: React.SyntheticEvent, value: any, reason?: AutocompleteChangeReason) => void;
    isOptionEqualToValue?: (option: any, value: any) => boolean;
    getOptionDisabled?: (option: any) => boolean;
    renderValue?: AutocompleteProps<any, any, any, any>['renderValue'];
}

export const CissAutoComplete = ({
    // identificação
    name,
    label,
    queryKey,
    objectId = '',
    // valores e opções
    value,
    options = [],
    requestOptions = null,
    // estados
    disabled = false,
    readOnly,
    multiple = true,
    disableClearable = false,
    disableCloseOnSelect,
    // estilo e layout
    sx,
    size = 'medium',
    error = '',
    // formatação de strings
    selectedStringTemplate = '',
    optionStringTemplate = '',
    getOptionString,
    getSelectedString,
    // callbacks e funções
    onChange,
    isOptionEqualToValue = () => false,
    getOptionDisabled,
    renderValue,
}: CissAutoCompleteProps) => {
    const requestListagem = useRequestListagem();
    const [searchQuery, setSearchQuery] = useState('');
    const [internalError, setInternalError] = useState('');
    const [page, setPage] = useState(1);

    let formattedRequestOptions: RequestOptionsType | null = null;

    if (requestOptions) {
        formattedRequestOptions = {
            limit: 50,
            ...requestOptions,
            page,
            query: searchQuery,
        };
    }

    const { data, refetch, isFetching } = useQuery(
        [queryKey, formattedRequestOptions],
        ({ signal }) => {
            if (formattedRequestOptions) {
                return requestListagem({ ...formattedRequestOptions, requestConfig: { signal } }).then((res) => res.data);
            }
        },
        {
            enabled: formattedRequestOptions !== null,
            keepPreviousData: true,
            onError: (err: any) => setInternalError(err.data?.message || ''),
            onSuccess: () => setInternalError(''),
        },
    );

    /**
     * Debounce de 500ms para evitar muitas requisições enquanto o usuário digita. <br>
     * Define a query de busca e reseta a página para 1.
     */
    const debouncedSetQuery = useDebouncedCallback((value: string) => {
        setSearchQuery(value);
        setPage(1);
    }, 500);

    /**
     * Ao expandir o autocomplete, reseta a query e volta à pagina 1.
     */
    const onOpenAutocomplete = () => {
        setSearchQuery('');
        setPage(1);
    };

    return (
        <Autocomplete
            loadingText=""
            noOptionsText="Sem opções"
            limitTags={1}
            sx={sx}
            size={size}
            readOnly={readOnly}
            disabled={disabled}
            multiple={multiple}
            onChange={onChange}
            loading={isFetching}
            renderValue={renderValue}
            onOpen={onOpenAutocomplete}
            disableClearable={disableClearable}
            getOptionDisabled={getOptionDisabled}
            isOptionEqualToValue={isOptionEqualToValue}
            disableCloseOnSelect={disableCloseOnSelect ?? multiple}
            value={!multiple && (!value || !Object.keys(value).length) ? null : value}
            options={formattedRequestOptions ? data?.data || [] : options}
            getOptionLabel={(option) => (getSelectedString ? getSelectedString(option) : getValueFromTemplate(selectedStringTemplate, option))}
            renderOption={(props, option: any) => (
                <Box component="li" {...props} key={objectId ? option[objectId] : Math.random()}>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: getOptionString ? getOptionString(option) : getValueFromTemplate(optionStringTemplate, option),
                        }}
                    />
                </Box>
            )}
            renderInput={(params) => (
                <CissTextField
                    {...params}
                    name={name}
                    label={label}
                    onChange={(e) => debouncedSetQuery(e.target.value)}
                    error={internalError || error}
                />
            )}
            slots={{
                paper: ({ children, ...paperProps }) => (
                    <Paper {...paperProps}>
                        <Box sx={{ position: 'relative' }}>
                            {children}

                            {formattedRequestOptions && isFetching && (
                                <Box
                                    onMouseDown={(event) => event.preventDefault()}
                                    sx={{
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 1,
                                        display: 'flex',
                                        position: 'absolute',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: (theme) => theme.alpha(theme.palette.common.white, 0.38),
                                    }}
                                >
                                    <CircularProgress size={34} />
                                </Box>
                            )}
                        </Box>

                        {formattedRequestOptions && (
                            <>
                                <Divider />

                                <Box
                                    onMouseDown={(event) => event.preventDefault()}
                                    sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        gap: 0.75,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <IconButton size="small" disabled={page === 1} onClick={() => setPage(1)}>
                                        <FirstPage />
                                    </IconButton>

                                    <IconButton size="small" disabled={page === 1} onClick={() => setPage((prev) => prev - 1)}>
                                        <ChevronLeft />
                                    </IconButton>

                                    <IconButton size="small" disabled={!data?.hasNext} onClick={() => setPage((prev) => prev + 1)}>
                                        <ChevronRight />
                                    </IconButton>

                                    <IconButton size="small" onClick={() => refetch()}>
                                        <Refresh />
                                    </IconButton>
                                </Box>
                            </>
                        )}
                    </Paper>
                ),
            }}
        />
    );
};
